import {
  ButtonLink,
  convertEventSelectToTarget,
  FormFeedBack,
  MainButton,
  removeSecondsFromFullTime,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import {
  CreateSemesterUseCaseInterface,
  GetAllSemestersFromEntityUseCaseInterface,
  GetAllShiftsUseCaseInterface,
  UpdateSemesterUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { clearUpdateSemesterModalData } from 'presentation/config/store/entities/entities-slice';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import * as Yup from 'yup';

interface IProps {
  isOpen: boolean;
  entityId: string;
  selectedSemesterIdRef: MutableRefObject<string | null>;
  toggle: () => void;
  requestGetAllSemestersFromEntity: (
    params: GetAllSemestersFromEntityUseCaseInterface.Params,
  ) => void;
}

export const CreateOrUpdateSemesterModal = ({
  isOpen,
  entityId,
  selectedSemesterIdRef,
  toggle,
  requestGetAllSemestersFromEntity,
}: IProps) => {
  // Hooks
  const dispatch = useDispatch();
  const { entitiesService, shiftsService } = useService();

  // States
  const { updateSemesterModalData } = useAppSelector((state) => state.Entities);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [shiftsSelectOptions, setShiftsSelectOptions] =
    useState<GetAllShiftsUseCaseInterface.Result>([]);
  const [requestGetAllShiftsStatus, setRequestGetAllShiftsStatus] = useState(
    STATUSPAGE.DONE,
  );
  const [requestCreateSemesterStatus, setRequestCreateSemesterStatus] =
    useState(STATUSPAGE.DONE);
  const [requestUpdateSemesterStatus, setRequestUpdateSemesterStatus] =
    useState(STATUSPAGE.DONE);

  // Constans
  const areInputsAndBtnsDisabled =
    requestCreateSemesterStatus === STATUSPAGE.LOADING ||
    requestUpdateSemesterStatus === STATUSPAGE.LOADING;

  // Methods
  const removeUpdateSemesterModalData = useCallback(() => {
    dispatch(clearUpdateSemesterModalData());
    selectedSemesterIdRef.current = null;
  }, []);

  const requestGetAllShifts = useCallback(async () => {
    setRequestGetAllShiftsStatus(STATUSPAGE.LOADING);
    await shiftsService
      .getAllShifts()
      .then((res) => {
        setShiftsSelectOptions(res);
      })
      .catch((err) => {})
      .finally(() => {
        setRequestGetAllShiftsStatus(STATUSPAGE.DONE);
      });
  }, []);

  const requestCreateSemester = useCallback(
    async (params: CreateSemesterUseCaseInterface.Params) => {
      setRequestCreateSemesterStatus(STATUSPAGE.LOADING);
      await entitiesService
        .createSemester(params)
        .then((res) => {
          toggle();
          notifySuccess(res.message);
          requestGetAllSemestersFromEntity({ entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestCreateSemesterStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );
  const requestUpdateSemester = useCallback(
    async (params: UpdateSemesterUseCaseInterface.Params) => {
      setRequestUpdateSemesterStatus(STATUSPAGE.LOADING);
      await entitiesService
        .updateSemester(params)
        .then((res) => {
          toggle();
          notifySuccess(res.message);
          requestGetAllSemestersFromEntity({ entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestUpdateSemesterStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: updateSemesterModalData?.name || '',
      shift: updateSemesterModalData?.shift
        ? {
            value: updateSemesterModalData?.shift.id,
            label: updateSemesterModalData?.shift.name,
          }
        : null,
      startingTime: updateSemesterModalData?.startingTime
        ? removeSecondsFromFullTime(updateSemesterModalData.startingTime)
        : '',
      finishingTime: updateSemesterModalData?.finishingTime
        ? removeSecondsFromFullTime(updateSemesterModalData.finishingTime)
        : '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campo é obrigatório'),
      shift: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.number(),
        })
        .required('Campo é obrigatório'),
      startingTime: Yup.string().notRequired(),
      finishingTime: Yup.string().notRequired(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entityId,
        name: values.name,
        shiftId: values.shift.value,
        ...(values.startingTime ? { startingTime: values.startingTime } : {}),
        ...(values.finishingTime
          ? { finishingTime: values.finishingTime }
          : {}),
      };

      selectedSemesterIdRef.current
        ? requestUpdateSemester({
            id: selectedSemesterIdRef.current,
            ...valuesToSubmit,
          })
        : requestCreateSemester(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllShifts();
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      id="addPersonToEntityModal"
      backdrop={'static'}
      onClosed={() => {
        removeUpdateSemesterModalData();
        validation.resetForm();
      }}
      onOpened={() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }}
    >
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalHeader toggle={toggle}>
          {selectedSemesterIdRef.current ? 'Atualizar' : 'Adicionar'} Semestre
        </ModalHeader>

        <ModalBody>
          <Row>
            <Col xs={12} className="mb-3">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                type="text"
                name="name"
                innerRef={nameInputRef}
                onBlur={validation.handleBlur}
                value={validation?.values?.name}
                onChange={validation.handleChange}
                disabled={areInputsAndBtnsDisabled}
                invalid={
                  validation?.touched?.name && !!validation?.errors?.name
                }
              />
              <FormFeedBack
                error={validation?.errors?.name}
                isInvalid={
                  validation?.touched?.name && !!validation?.errors?.name
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="sex">Turno</Label>
              <Select
                isClearable
                id="shift"
                name="shift"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation?.values?.shift}
                options={shiftsSelectOptions.map((shiftOption) => {
                  return {
                    value: shiftOption.id,
                    label: shiftOption.name,
                  };
                })}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('shift', e),
                  );
                }}
                isLoading={requestGetAllShiftsStatus === STATUSPAGE.LOADING}
                isDisabled={
                  areInputsAndBtnsDisabled ||
                  requestGetAllShiftsStatus === STATUSPAGE.LOADING
                }
                invalid={
                  !!(validation?.touched?.shift && validation?.errors?.shift)
                }
              />
              <FormFeedBack
                error={String(validation.errors?.shift)}
                isInvalid={
                  !!(validation.touched?.shift && validation.errors?.shift)
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="email">Horário inicial</Label>
              <Input
                type="time"
                id="startingTime"
                name="startingTime"
                onBlur={validation.handleBlur}
                value={validation?.values?.startingTime}
                onChange={validation.handleChange}
                disabled={areInputsAndBtnsDisabled}
                invalid={
                  validation?.touched?.startingTime &&
                  !!validation?.errors?.startingTime
                }
              />
              <FormFeedBack
                error={validation?.errors?.startingTime}
                isInvalid={
                  validation?.touched?.startingTime &&
                  !!validation?.errors?.startingTime
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="email">Horário final</Label>
              <Input
                type="time"
                id="finishingTime"
                name="finishingTime"
                onBlur={validation.handleBlur}
                value={validation?.values?.finishingTime}
                onChange={validation.handleChange}
                disabled={areInputsAndBtnsDisabled}
                invalid={
                  validation?.touched?.finishingTime &&
                  !!validation?.errors?.finishingTime
                }
              />
              <FormFeedBack
                error={validation?.errors?.finishingTime}
                isInvalid={
                  validation?.touched?.finishingTime &&
                  !!validation?.errors?.finishingTime
                }
              />
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <ButtonLink
            type="button"
            onClick={toggle}
            disabled={areInputsAndBtnsDisabled}
          >
            Cancelar
          </ButtonLink>
          <MainButton
            type="submit"
            isDisabled={areInputsAndBtnsDisabled}
            isLoading={areInputsAndBtnsDisabled}
          >
            Salvar
          </MainButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
